// The sign-in page's brand-panel texts as the PLATFORM OPERATOR has set them (owner's decision B1,
// 4.11.2026) — the READ half, for the pre-session surface.
//
//   GET /api/auth/sign-in-texts -> 200 { taglineFi, taglineEn, taglineSubFi, taglineSubEn }
//
// ANONYMOUS, and with no tenant anywhere in it. These texts are the PLATFORM's, identical for every
// customer and for the operator console's own login, so the route takes no `X-Tenant`, no bearer and no
// cookie — and on a page nobody has signed into `apiFetch` adds none of its own
// (backend/src/Clms.Api/Security/AuthEndpoints.cs). That is what makes it callable from the one surface
// which by definition has no session.
//
// A NULL IS NOT AN EMPTY PANEL. Null means "the operator has set nothing", and the text in force is then
// the translation file's own `auth.tagline` / `auth.taglineSub`. Deciding that is `./signInTextRules`, a
// pure module this one re-exports, so the table has a test that needs neither a browser nor Vite.

import { useEffect, useState } from "react";
import type { components } from "@clms/contracts";
import { apiFetch } from "../lib/api";
import type { SignInTextsState } from "./signInTextRules";

export {
  NO_SIGN_IN_TEXTS,
  resolveSignInTexts,
  signInTextLanguage,
  type SignInTextResolution,
  type SignInTexts,
  type SignInTextsState,
} from "./signInTextRules";

type PlatformSignInTextsDto = components["schemas"]["PlatformSignInTextsDto"];

/** A state the read can settle on. */
type SettledSignInTextsState = Extract<SignInTextsState, { status: "ready" | "error" }>;

/**
 * One in-flight/settled read for the lifetime of the document. The layout is the only caller today, but
 * the sign-in surfaces mount and unmount under it (login, email-link request, redeem, callback), and a
 * second HTTP call per hop would be the only visible effect. A full page load clears it, which is the
 * refresh path; `error` is evicted on settle so the next mount retries rather than caching a blip.
 *
 * A single slot and not a map, unlike `tenantBranding`'s: these texts are a platform singleton, so there
 * is no key to cache them under.
 */
let inFlight: Promise<SettledSignInTextsState> | null = null;

/** Read the texts in force. Never throws — every outcome is a state. */
export function fetchSignInTexts(): Promise<SettledSignInTextsState> {
  if (inFlight) return inFlight;

  const pending = apiFetch<PlatformSignInTextsDto>("/api/auth/sign-in-texts")
    .then<SettledSignInTextsState>((body) => ({
      status: "ready",
      texts: {
        taglineFi: body.taglineFi ?? null,
        taglineEn: body.taglineEn ?? null,
        taglineSubFi: body.taglineSubFi ?? null,
        taglineSubEn: body.taglineSubEn ?? null,
      },
    }))
    .catch<SettledSignInTextsState>(() => {
      inFlight = null; // transient — let the next mount try again
      return { status: "error" };
    });

  inFlight = pending;
  return pending;
}

/** Test seam: drop the memoized read (the SPA itself never needs this — a reload does it). */
export function resetSignInTextsCache(): void {
  inFlight = null;
}

/** The texts in force, read once per document however many components ask for them. */
export function useSignInTexts(): SignInTextsState {
  const [state, setState] = useState<SignInTextsState>({ status: "loading" });

  useEffect(() => {
    let active = true;
    void fetchSignInTexts().then((settled) => {
      if (active) setState(settled);
    });
    return () => {
      active = false;
    };
  }, []);

  return state;
}
